import { Fragment } from 'react';
import { Dialog, Listbox, Menu, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, DotsVerticalIcon } from '@heroicons/react/solid';

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export function Page({ description, actions, children }) {
  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {(description || actions) && (
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          {description && <p className="max-w-2xl text-sm text-slate-500">{description}</p>}
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function Card({ className = '', children }) {
  return (
    <div className={classNames('rounded-2xl border border-slate-200/80 bg-white shadow-sm', className)}>
      {children}
    </div>
  );
}

export function Button({ variant = 'primary', className = '', type = 'button', children, ...props }) {
  const styles = {
    primary:
      'bg-brand-600 text-white hover:bg-brand-700 focus:ring-brand-500 shadow-sm',
    secondary:
      'bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 focus:ring-brand-500',
    ghost: 'text-slate-600 hover:bg-slate-100 focus:ring-brand-500',
    danger: 'bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-500',
  };
  return (
    <button
      type={type}
      className={classNames(
        'inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50',
        styles[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Badge({ tone = 'neutral', children }) {
  const tones = {
    success: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    warning: 'bg-amber-50 text-amber-800 ring-amber-600/20',
    danger: 'bg-rose-50 text-rose-700 ring-rose-600/20',
    neutral: 'bg-slate-100 text-slate-600 ring-slate-500/10',
  };
  return (
    <span className={classNames('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset', tones[tone])}>
      {children}
    </span>
  );
}

export function Field({ label, error, hint, children }) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-slate-700">{label}</label>}
      <div className={label ? 'mt-1.5' : ''}>{children}</div>
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
      {hint && !error && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export const inputClass =
  'block w-full rounded-xl border-slate-200 bg-white text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-500 focus:ring-brand-500';

export function Select({ label, value, onChange, options, display = (o) => o?.nombre || o?.name || '', placeholder = 'Seleccionar' }) {
  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div>
          {label && <Listbox.Label className="block text-sm font-medium text-slate-700">{label}</Listbox.Label>}
          <div className={classNames('relative', label && 'mt-1.5')}>
            <Listbox.Button className={classNames(inputClass, 'cursor-default py-2 pl-3 pr-10 text-left')}>
              <span className="block truncate">{display(value) || placeholder}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDownIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
              </span>
            </Listbox.Button>
            <Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
              <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                {options.map((option) => (
                  <Listbox.Option
                    key={option.id ?? option.nombre}
                    value={option}
                    className={({ active }) =>
                      classNames(active ? 'bg-brand-50 text-brand-900' : 'text-slate-900', 'relative cursor-default select-none py-2 pl-3 pr-9')
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>{display(option)}</span>
                        {selected && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-brand-600">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </div>
      )}
    </Listbox>
  );
}

export function Modal({ open, onClose, title, children, footer }) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg overflow-hidden rounded-2xl bg-white p-6 shadow-xl">
                {title && (
                  <Dialog.Title as="h3" className="text-lg font-semibold text-slate-900">
                    {title}
                  </Dialog.Title>
                )}
                <div className="mt-5 space-y-4">{children}</div>
                {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export function EmptyState({ title, description }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
      <p className="text-sm font-medium text-slate-900">{title}</p>
      {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
    </div>
  );
}

export function KebabMenu({ items }) {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-500">
        <span className="sr-only">Opciones</span>
        <DotsVerticalIcon className="h-5 w-5" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-20 mt-1 w-48 origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
          {items.map((item) => (
            <Menu.Item key={item.label} disabled={item.disabled}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={item.onClick}
                  className={classNames(
                    active ? 'bg-slate-50' : '',
                    item.danger ? 'text-rose-600' : 'text-slate-700',
                    'block w-full px-4 py-2 text-left text-sm'
                  )}
                >
                  {item.label}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
